import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, WifiOff } from 'lucide-react';
import { mozoApi, mensajeDeError } from './mozoApi';
import usePolling from './usePolling';
import MozoPedido from './MozoPedido';
import MozoCuenta from './MozoCuenta';

const useEnLinea = () => {
  const [enLinea, setEnLinea] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );

  useEffect(() => {
    const subir = () => setEnLinea(true);
    const bajar = () => setEnLinea(false);

    window.addEventListener('online', subir);
    window.addEventListener('offline', bajar);

    return () => {
      window.removeEventListener('online', subir);
      window.removeEventListener('offline', bajar);
    };
  }, []);

  return enLinea;
};

export default function MozoMesa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const enLinea = useEnLinea();
  const [operacion, setOperacion] = useState(null);
  const [error, setError] = useState('');
  const [pestana, setPestana] = useState('pedido');
  const [comensales, setComensales] = useState(2);
  const [abriendo, setAbriendo] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await mozoApi.get(`/restaurante/mesas/${id}/operacion`);

      setOperacion(data);
      setError('');
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo cargar la mesa.'));
    }
  }, [id]);

  usePolling(cargar, 10000);

  useEffect(() => {
    setOperacion(null);
    setPestana('pedido');
  }, [id]);

  const abrir = async () => {
    if (abriendo) return;

    setAbriendo(true);
    setError('');

    try {
      await mozoApi.post(`/restaurante/mesas/${id}/abrir`, { comensales });
      await cargar();
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo abrir la mesa.'));
    } finally {
      setAbriendo(false);
    }
  };

  const mesa = operacion?.mesa;
  const sesion = operacion?.sesion;
  const cuenta = operacion?.cuentas?.[0];
  const titulo = mesa ? mesa.alias || mesa.nombre : 'Mesa';

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="sticky top-0 z-10 flex items-center gap-3 bg-green-800 px-4 py-3 text-white">
        <Link to="/mozo" aria-label="Volver a las mesas" className="rounded-lg bg-white/15 p-2">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <p className="text-lg font-extrabold leading-tight">{titulo}</p>
          {sesion && <p className="text-xs opacity-80">{sesion.comensales} comensales</p>}
        </div>
      </header>

      {!enLinea && (
        <p className="flex items-center gap-2 bg-red-600 px-4 py-2 text-sm font-bold text-white">
          <WifiOff size={16} /> Sin conexión. Los cambios no se guardan hasta que vuelva el wifi.
        </p>
      )}

      {error && (
        <p role="alert" className="bg-red-50 px-4 py-2 text-sm font-bold text-red-700">
          {error}
        </p>
      )}

      <main className="px-4 pt-4">
        {operacion && !sesion && (
          <section className="rounded-2xl bg-white p-5 shadow-md">
            <h2 className="text-lg font-extrabold text-gray-900">Abrir mesa</h2>
            <p className="mt-1 text-sm text-gray-600">¿Cuántos comensales?</p>
            <div className="mt-4 flex items-center justify-center gap-6">
              <button
                type="button"
                aria-label="Menos comensales"
                onClick={() => setComensales((c) => Math.max(1, c - 1))}
                className="rounded-xl bg-gray-100 p-4"
              >
                <Minus size={24} />
              </button>
              <span className="w-12 text-center text-4xl font-extrabold">{comensales}</span>
              <button
                type="button"
                aria-label="Más comensales"
                onClick={() => setComensales((c) => Math.min(99, c + 1))}
                className="rounded-xl bg-gray-100 p-4"
              >
                <Plus size={24} />
              </button>
            </div>
            <button
              type="button"
              onClick={abrir}
              disabled={abriendo || !enLinea}
              className="mt-6 w-full rounded-xl bg-green-700 py-4 text-base font-extrabold text-white disabled:opacity-50"
            >
              {abriendo ? 'Abriendo...' : 'Abrir mesa'}
            </button>
          </section>
        )}

        {sesion && cuenta && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl bg-gray-200 p-1">
              {[
                ['pedido', 'Pedido'],
                ['cuenta', 'Cuenta'],
              ].map(([clave, texto]) => (
                <button
                  key={clave}
                  type="button"
                  onClick={() => setPestana(clave)}
                  className={`rounded-lg py-2 text-sm font-extrabold ${
                    pestana === clave ? 'bg-white text-green-800 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  {texto}
                </button>
              ))}
            </div>

            {pestana === 'pedido' ? (
              <MozoPedido
                sesionId={sesion.id}
                cuentaId={cuenta.id}
                enLinea={enLinea}
                onEnviado={() => {
                  cargar();
                  setPestana('cuenta');
                }}
              />
            ) : (
              <MozoCuenta
                cuentaId={cuenta.id}
                pedidos={operacion.pedidos || []}
                enLinea={enLinea}
                onCambio={cargar}
                onMesaLiberada={() =>
                  navigate('/mozo', {
                    replace: true,
                    state: { aviso: `Cuenta de ${titulo} cobrada y mesa liberada.` },
                  })
                }
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}
